import React, { Component, Fragment } from 'react';
import b from 'b_';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { LogoProofpointWord } from '../Logo/PfptLogo';
import Icon from '../Icon/Icon';
import { Key } from '../../mirror/userInput';
import AppSwitcherStore from '../../stores/AppSwitcherStore';
import { FTBody, FTTitle } from '../Fuse/Text';
import UDSpinner from '../UD/Fragments/UDSpinner';
import './AppSwitcher.css';

const block = b.lock('AppSwitcher');

class AppSwitcher extends Component {
    static propTypes = {
        store: AppSwitcherStore.PropType.isRequired,
        className: PropTypes.string,
        toggleClassName: PropTypes.string,
        modalClassName: PropTypes.string,
    };

    appActionRefs = [];

    state = {
        focusedApp: null,
    };

    componentWillMount() {
        this.props.store.loadApps().catch((error) => window.console.error(`Failed to load apps. ${error?.message}.`));

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    onRef = (element) => {
        this.container = element;
    };

    onAppActionRef = (element, appIndex) => {
        this.appActionRefs[appIndex] = element;
    };

    onDocumentClick = ({ target }) => {
        if (this.container && !this.container.contains(target)) {
            this.props.store.modal.close();
        }
    };

    handleKeyDown = (event) => {
        if (event.keyCode === Key.ESC) {
            this.props.store.modal.close();
        }
        const { modal, apps, selectedApp } = this.props.store;
        if (modal.isOpen && apps.length > 0) {
            const moveFromApp = this.state.focusedApp || selectedApp;
            if (event.keyCode === Key.DOWN) {
                if (moveFromApp == null) {
                    this.focusApp(0);
                } else if (moveFromApp < apps.length - 1) {
                    this.focusApp(moveFromApp + 1);
                }
            } else if (event.keyCode === Key.UP) {
                if (moveFromApp == null) {
                    this.focusApp(apps.length - 1);
                } else if (moveFromApp > 0) {
                    this.focusApp(moveFromApp - 1);
                }
            }
        }
    };

    selectApp = (event, appIndex) => {
        if (this.props.store.selectedApp === appIndex) {
            event.preventDefault();
        }
    };

    focusApp = (appIndex) => {
        this.appActionRefs[appIndex].focus();
    };

    isAppSelected = (appIndex) => {
        return this.props.store.selectedApp === appIndex;
    };

    onAppFocused = (appIndex) => {
        this.setState({ focusedApp: appIndex });
    };

    renderApps() {
        const { store } = this.props;

        if (store.loadStatus.isLoading) {
            return <UDSpinner type={'fuse'} className={block('Loader')} />;
        }

        if (store.loadStatus.isFailed) {
            return <div className={block('ErrorContainer')}>
                <Icon className={block('ErrorIcon')} type={'error_outline'} />
                <FTBody className={block('ErrorMessage')}>
                    An unknown error occurred while loading your applications. Please refresh the page to try again.
                </FTBody>
            </div>;
        }

        return <Fragment>
            <FTTitle className={block('SectionName')}>Apps</FTTitle>
            {(store.apps || []).map((app, appIndex) => (
                <a
                    key={app.name}
                    className={block('AppAction', { selected: this.isAppSelected(appIndex) })}
                    ref={(element) => this.onAppActionRef(element, appIndex)}
                    onFocus={() => this.onAppFocused(appIndex)}
                    onClick={(event) => this.selectApp(event, appIndex)}
                    href={app.link}
                    {...(!store.modal.isOpen ? { tabIndex: '-1' } : {})}
                >
                    <div tabIndex={'-1'} className={block('AppActionContent')}>
                        {app.icon?.kind === 'font:material' ?
                            <span className={cn('material-icon', block('AppIcon'))}>{app.icon.name}</span> : null}
                        <FTBody className={block('AppName')}>{app.name}</FTBody>
                    </div>
                </a>
            ))}
        </Fragment>;
    }

    render() {
        const { store, className, toggleClassName, modalClassName } = this.props;

        return <div ref={this.onRef} className={cn(block({ open: store.modal.isOpen }), className)}>
            <button className={cn(block('Toggle'), toggleClassName)} onClick={store.modal.toggle}>
                <Icon tabIndex={'-1'} type={'apps'} className={block('ToggleIcon')} />
            </button>

            <div className={cn(block('Modal'), modalClassName)}>
                <div className={block('TopBar')}>
                    <button
                        className={cn(block('Toggle', { modal: true }), toggleClassName)}
                        onClick={store.modal.toggle}
                    >
                        <Icon tabIndex={'-1'} type={'apps'} className={block('ToggleIcon')} />
                    </button>
                    <div className={block('Logo')}>
                        <LogoProofpointWord
                            mainColor={store.modal.isOpen ? '#757575' : 'white'}
                            style={{
                                width: 98,
                                height: 20,
                                display: 'block',
                            }}
                        />
                    </div>
                    <div className={block('AppName')}>Isolation</div>
                </div>
                {this.renderApps()}
            </div>
        </div>;
    }
}

export default observer(AppSwitcher);
